import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { deals, stores } from '../data/mockData';
import { ShoppingCart, Ticket, Copy, ExternalLink, Heart } from 'lucide-react';
import { toast } from 'sonner';
import DealDialog from './DealDialog';
import { useAuth } from '../context/AuthContext';

const DealsSection = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [revealedCoupons, setRevealedCoupons] = useState({});
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loadingFavorite, setLoadingFavorite] = useState(null);
  
  const { isAuthenticated, isFavorite, addFavorite, removeFavorite } = useAuth();

  const handleToggleFavorite = async (e, dealId) => {
    e.stopPropagation();
    e.preventDefault();
    
    if (!isAuthenticated) {
      toast.error('Please sign in to save favorites');
      return;
    }
    
    setLoadingFavorite(dealId);
    try {
      if (isFavorite(dealId)) {
        await removeFavorite(dealId);
        toast.success('Removed from favorites');
      } else {
        await addFavorite(dealId);
        toast.success('Added to favorites!');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoadingFavorite(null);
    }
  };

  const tabs = [
    { id: 'all', label: 'Show all' },
    { id: 'popular', label: 'Popular' },
    { id: 'hottest', label: 'Hottest' },
    { id: 'expiring', label: 'Expired Soon' },
  ];

  // Create a prioritized deals list with Fashion and Cosmetics mixed in
  const prioritizedDeals = useMemo(() => {
    // Get fashion and cosmetics deals
    const fashionDeals = deals.filter(d => d.category === 'fashion');
    const cosmeticsDeals = deals.filter(d => d.category === 'cosmetics');
    const otherDeals = deals.filter(d => d.category !== 'fashion' && d.category !== 'cosmetics');
    
    // Mix them: fashion, cosmetics, other, fashion, cosmetics, other...
    const mixed = [];
    const maxLen = Math.max(fashionDeals.length, cosmeticsDeals.length, otherDeals.length);
    
    for (let i = 0; i < maxLen; i++) {
      if (fashionDeals[i]) mixed.push(fashionDeals[i]);
      if (cosmeticsDeals[i]) mixed.push(cosmeticsDeals[i]);
      if (otherDeals[i]) mixed.push(otherDeals[i]);
      if (otherDeals[i + maxLen]) mixed.push(otherDeals[i + maxLen]);
    }
    
    return mixed;
  }, []);

  const filteredDeals = prioritizedDeals.filter((deal) => {
    let tabMatch = true;
    if (activeTab === 'popular') tabMatch = deal.popular;
    else if (activeTab === 'hottest') tabMatch = deal.hottest;
    else if (activeTab === 'expiring') tabMatch = deal.expiringSoon;

    let categoryMatch = true;
    if (selectedCategory !== 'all') categoryMatch = deal.store === selectedCategory;

    return tabMatch && categoryMatch;
  }).slice(0, 15); // Limit to 15 deals on homepage

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      return false;
    }
  };

  const handleBuyNow = (deal) => {
    const store = stores.find(s => s.slug === deal.store);
    setSelectedDeal({ ...deal, storeName: store?.name });
    setDialogOpen(true);
  };

  const handleRevealCoupon = async (deal) => {
    setRevealedCoupons(prev => ({
      ...prev,
      [deal.id]: true
    }));

    const copied = await copyToClipboard(deal.couponCode);
    toast.success(
      <div className="flex flex-col">
        <span className="font-semibold flex items-center gap-2">
          <Copy size={16} /> Coupon Code {copied ? 'Copied!' : 'Revealed!'}
        </span>
        <span className="text-lg font-mono bg-gray-100 px-2 py-1 rounded mt-1">{deal.couponCode}</span>
        <span className="text-sm text-gray-500 mt-1">Use at {deal.store.charAt(0).toUpperCase() + deal.store.slice(1)}</span>
      </div>,
      { duration: 5000 }
    );
  };

  const handleGoToStore = (deal) => {
    const store = stores.find(s => s.slug === deal.store);
    setSelectedDeal({ ...deal, storeName: store?.name });
    setDialogOpen(true);
  };

  const getStoreName = (storeSlug) => {
    const store = stores.find(s => s.slug === storeSlug);
    return store?.name || storeSlug;
  };

  const renderButton = (deal) => {
    const isRevealed = revealedCoupons[deal.id];

    switch (deal.buttonType) {
      case 'buy':
        return (
          <button 
            onClick={() => handleBuyNow(deal)}
            className="w-full bg-[#000000] text-white py-2.5 rounded font-medium text-sm hover:bg-[#000000] transition-colors flex items-center justify-center gap-2 active:scale-95"
          >
            <ShoppingCart size={16} />
            BUY IT NOW
          </button>
        );
      case 'bestDeal':
        return (
          <button 
            onClick={() => handleBuyNow(deal)}
            className="w-full bg-[#000000] text-white py-2.5 rounded font-medium text-sm hover:bg-[#000000] transition-colors active:scale-95"
          >
            BEST DEAL TODAY
          </button>
        );
      case 'coupon':
        if (isRevealed) {
          return (
            <div className="space-y-1.5 md:space-y-2">
              <div className="w-full bg-gray-100 border-2 border-dashed border-[#000000] text-gray-800 py-1.5 md:py-2 rounded font-mono text-center font-bold text-xs md:text-sm">
                {deal.couponCode}
              </div>
              <button 
                onClick={() => handleGoToStore(deal)}
                className="w-full bg-[#000000] text-white py-1.5 md:py-2 rounded font-medium text-[10px] md:text-xs hover:bg-[#000000] transition-colors flex items-center justify-center gap-1"
              >
                <ExternalLink size={12} className="md:w-3.5 md:h-3.5" />
                GO TO STORE
              </button>
            </div>
          );
        }
        return (
          <button 
            onClick={() => handleRevealCoupon(deal)}
            className="w-full bg-[#000000]/10 border-2 border-dashed border-[#000000] text-gray-800 py-2 md:py-2.5 rounded font-medium text-[10px] md:text-sm hover:bg-[#000000]/20 transition-colors flex items-center justify-center gap-1.5 md:gap-2 active:scale-95"
          >
            <Ticket size={14} className="md:w-4 md:h-4" />
            REVEAL COUPON
          </button>
        );
      default:
        return (
          <button 
            onClick={() => handleBuyNow(deal)}
            className="w-full bg-[#000000] text-white py-2 md:py-2.5 rounded font-medium text-[10px] md:text-sm hover:bg-[#000000] transition-colors active:scale-95"
          >
            BUY IT NOW
          </button>
        );
    }
  };

  return (
    <section className="py-6 md:py-8 bg-white">
      <div className="max-w-7xl mx-auto px-3 md:px-4">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 md:mb-6">
          <div className="flex items-center gap-6 mb-4 md:mb-0">
            <h2 className="text-xl md:text-2xl font-bold text-gray-800">Top Deals</h2>
            <div className="flex flex-wrap gap-1 md:gap-4">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`text-sm px-2 py-1 md:px-3 md:py-1 rounded transition-colors ${
                    activeTab === tab.id
                      ? 'text-gray-800 border-b-2 border-gray-800 font-medium'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Category Dropdown */}
          <div className="relative">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 px-4 py-2 rounded"
            >
              Choose category +
            </button>
            {dropdownOpen && (
              <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-2 w-48 z-20 max-h-64 overflow-y-auto">
                <button
                  onClick={() => {
                    setSelectedCategory('all');
                    setDropdownOpen(false);
                  }}
                  className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                    selectedCategory === 'all' ? 'text-gray-800 font-medium' : 'text-gray-700'
                  }`}
                >
                  All categories
                </button>
                {stores.map((store) => (
                  <button
                    key={store.id}
                    onClick={() => {
                      setSelectedCategory(store.slug);
                      setDropdownOpen(false);
                    }}
                    className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                      selectedCategory === store.slug ? 'text-gray-800 font-medium' : 'text-gray-700'
                    }`}
                  >
                    {store.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Deals Grid - Smaller cards on mobile */}
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4">
          {filteredDeals.map((deal, index) => (
            <div
              key={deal.id}
              className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-all duration-300 group card-hover animate-fadeInUp opacity-0"
              style={{ animationDelay: `${index * 0.05}s`, animationFillMode: 'forwards' }}
            >
              {/* Image Container - Smaller on mobile */}
              <div className="relative aspect-square bg-gray-50 p-2 md:p-4 overflow-hidden">
                {/* Badge - Top Left */}
                {deal.badge && (
                  <div className="absolute top-0 left-0 z-10">
                    <div className={`text-white text-[8px] md:text-[10px] font-medium px-1.5 md:px-2 py-0.5 md:py-1 ${
                      deal.badge === 'Mega Deal' ? 'bg-orange-500' :
                      deal.badge === 'Hot Deal' ? 'bg-orange-600' :
                      deal.badge === 'Best price' ? 'bg-blue-500' :
                      deal.badge === 'Clearance' ? 'bg-purple-500' :
                      deal.badge === 'Trending' ? 'bg-pink-500' :
                      deal.badge === 'Limited Stock' ? 'bg-yellow-600' :
                      'bg-[#000000]'
                    }`}>
                      {deal.badge}
                    </div>
                  </div>
                )}

                {/* Favorite Button - Top Right (moved to avoid overlap) */}
                <button
                  onClick={(e) => handleToggleFavorite(e, deal.id)}
                  disabled={loadingFavorite === deal.id}
                  className={`absolute top-1.5 md:top-2 right-10 md:right-12 z-20 w-6 h-6 md:w-8 md:h-8 rounded-full flex items-center justify-center transition-all ${
                    isFavorite(deal.id) 
                      ? 'bg-black text-white' 
                      : 'bg-white/90 text-gray-400 hover:text-black hover:bg-white shadow-sm'
                  }`}
                  data-testid={`favorite-btn-${deal.id}`}
                >
                  {loadingFavorite === deal.id ? (
                    <div className="w-3 h-3 md:w-4 md:h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Heart size={14} className="md:w-4 md:h-4" fill={isFavorite(deal.id) ? 'currentColor' : 'none'} />
                  )}
                </button>

                {/* Discount Badge - Top Right */}
                <div className="absolute top-1.5 md:top-2 right-1.5 md:right-2 bg-[#000000] text-white text-[10px] md:text-xs font-bold px-1.5 md:px-2 py-0.5 md:py-1 rounded z-10">
                  -{deal.discount}%
                </div>

                {/* Product Image */}
                <img
                  src={deal.image}
                  alt={deal.title}
                  className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-300"
                  onError={(e) => {
                    e.target.src = 'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=300&h=300&fit=crop';
                  }}
                />
              </div>

              {/* Content - Smaller padding on mobile */}
              <div className="p-2 md:p-4">
                <h3 className="text-xs md:text-sm font-medium text-gray-800 line-clamp-2 mb-1 min-h-[32px] md:min-h-[40px] group-hover:text-gray-800 transition-colors">
                  {deal.title}
                </h3>
                
                {/* Price - Smaller on mobile */}
                <div className="flex items-center gap-1 md:gap-2 mb-1">
                  <span className="text-sm md:text-lg font-bold text-gray-800">${deal.salePrice?.toFixed(2)}</span>
                  <span className="text-[10px] md:text-sm text-gray-400 line-through">${deal.originalPrice?.toFixed(2)}</span>
                </div>

                <p className="text-gray-800 text-[10px] md:text-xs mb-2 md:mb-3 line-clamp-1">{deal.cashback}</p>
                
                {renderButton(deal)}

                <Link
                  to={`/store/${deal.store}`}
                  className="block text-center text-gray-800 text-[10px] md:text-xs mt-1.5 md:mt-2 hover:underline"
                >
                  See All {getStoreName(deal.store)} offers
                </Link>
              </div>
            </div>
          ))}
        </div>

        {filteredDeals.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No deals found for the selected filters.
          </div>
        )}
      </div>

      {/* Deal Dialog */}
      <DealDialog
        deal={selectedDeal}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        storeName={selectedDeal?.storeName}
      />
    </section>
  );
};

export default DealsSection;
